import { createRoute, lazyRouteComponent, notFound } from '@tanstack/react-router';
import { isApiError } from '@/api/errors';
import { labelFrom } from '@/app/breadcrumbs';
import { appRoute, type RouterContext } from '@/app/layout-routes';
import { requirePermissions } from '@/app/route-guards';
import { operatorQuery, operatorsListQuery, operatorsSearchSchema } from './api';
import { OperatorNotFound } from './pages/operator-not-found';
import { operatorDetailSearchSchema } from './tabs';

/** Operator ids are positive integers; anything else is a missing page, not a request. */
const parseId = (raw: string) => {
  const id = Number(raw);
  if (!Number.isInteger(id) || id <= 0) throw notFound();
  return id;
};

async function loadOperator(context: RouterContext, rawId: string) {
  try {
    return await context.queryClient.ensureQueryData(operatorQuery(parseId(rawId)));
  } catch (error) {
    if (isApiError(error) && error.code === 'not_found') throw notFound();
    throw error;
  }
}

const crumb = (params: Record<string, string>, operator: unknown) => ({
  label: labelFrom(operator, 'Name') ?? `Operator #${params.operatorId ?? ''}`,
});

export const operatorsListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'operators',
  validateSearch: operatorsSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_LIST_OPERATORS'),
  loaderDeps: ({ search }) => search,
  // Starts the fetch without blocking: the page shows the previous rows, dimmed, until it lands.
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(operatorsListQuery(deps));
  },
  component: lazyRouteComponent(() => import('./pages/operators-list-page'), 'OperatorsListPage'),
});

export const operatorCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'operators/new',
  beforeLoad: ({ context }) => requirePermissions(context, 'UPDATE_OPERATORS'),
  staticData: { crumbs: () => [{ label: 'New operator' }] },
  component: lazyRouteComponent(() => import('./pages/operator-create-page'), 'OperatorCreatePage'),
});

export const operatorDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'operators/$operatorId',
  validateSearch: operatorDetailSearchSchema,
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_OPERATORS'),
  loader: ({ context, params }) => loadOperator(context, params.operatorId),
  staticData: { crumbs: (params, operator) => [crumb(params, operator)] },
  notFoundComponent: OperatorNotFound,
  component: lazyRouteComponent(() => import('./pages/operator-detail-page'), 'OperatorDetailPage'),
});

export const operatorEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'operators/$operatorId/edit',
  beforeLoad: ({ context }) => requirePermissions(context, 'VIEW_SINGLE_OPERATORS', 'UPDATE_OPERATORS'),
  loader: ({ context, params }) => loadOperator(context, params.operatorId),
  staticData: {
    crumbs: (params, operator) => [
      { ...crumb(params, operator), href: `/operators/${encodeURIComponent(params.operatorId ?? '')}` },
      { label: 'Edit' },
    ],
  },
  notFoundComponent: OperatorNotFound,
  component: lazyRouteComponent(() => import('./pages/operator-edit-page'), 'OperatorEditPage'),
});

/** Routes for this feature, attached to `appRoute`; `app/routeTree.ts` spreads them in. */
export const operatorsRoutes = [
  operatorsListRoute,
  operatorCreateRoute,
  operatorDetailRoute,
  operatorEditRoute,
] as const;
